import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Pagination } from "@/components/ui/pagination";
import { useAuditLogs } from "@/hooks/api/useAuditLogs";

const PAGE_SIZE = 20;

const ACTION_COLORS: Record<string, string> = {
  create: "bg-green-100 text-green-800",
  update: "bg-blue-100 text-blue-800",
  delete: "bg-red-100 text-red-800",
  complete: "bg-gray-100 text-gray-700",
  activate: "bg-green-100 text-green-800",
  deactivate: "bg-orange-100 text-orange-800",
  audio: "bg-purple-100 text-purple-800",
};

function actionColor(action: string): string {
  const verb = action.split(".")[1] ?? "";
  return ACTION_COLORS[verb] ?? "bg-secondary text-secondary-foreground";
}

type AuditMeta = {
  patientName?: string;
  personalNumber?: string;
  patientId?: string;
  templateName?: string;
  userName?: string;
  userEmail?: string;
  specialityName?: string;
  durationSeconds?: number;
};

function ResourceDetails({
  resourceType,
  resourceId,
  metadata,
  t,
}: {
  resourceType: string | null;
  resourceId: string | null;
  metadata: AuditMeta | null;
  t: (key: string) => string;
}) {
  const meta = metadata ?? {};
  const rows: React.ReactNode[] = [];

  // Patient info (with or without metadata)
  if (resourceType === "patient" && resourceId) {
    rows.push(
      <div key="patient" className="flex items-center gap-2 flex-wrap">
        <span className="text-muted-foreground">{t("admin.auditLog.details.patient")}:</span>
        {meta.patientName ? (
          <span className="font-medium">{meta.patientName}</span>
        ) : null}
        {meta.personalNumber && (
          <span className="text-muted-foreground">({meta.personalNumber})</span>
        )}
        <Link
          to={`/patients/${resourceId}`}
          className="inline-flex items-center gap-1 text-primary hover:underline text-xs"
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="h-3 w-3" />
          {t("admin.auditLog.details.goToPatient")}
        </Link>
      </div>,
    );
  }

  // Consultation info (with or without metadata)
  if (resourceType === "consultation" && resourceId) {
    if (meta.patientName) {
      rows.push(
        <div key="consultation-patient" className="flex items-center gap-2 flex-wrap">
          <span className="text-muted-foreground">{t("admin.auditLog.details.patient")}:</span>
          <span className="font-medium">{meta.patientName}</span>
          {meta.patientId && (
            <Link
              to={`/patients/${meta.patientId}`}
              className="inline-flex items-center gap-1 text-primary hover:underline text-xs"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="h-3 w-3" />
              {t("admin.auditLog.details.goToPatient")}
            </Link>
          )}
        </div>,
      );
    }
    rows.push(
      <div key="consultation" className="flex items-center gap-2">
        <span className="text-muted-foreground">{t("admin.auditLog.details.consultation")}:</span>
        <Link
          to={`/consultation/${resourceId}`}
          className="inline-flex items-center gap-1 text-primary hover:underline text-xs"
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="h-3 w-3" />
          {t("admin.auditLog.details.goToConsultation")}
        </Link>
      </div>,
    );
  }

  // Template actions
  if (meta.templateName) {
    rows.push(
      <div key="template" className="flex items-center gap-2">
        <span className="text-muted-foreground">{t("admin.auditLog.details.template")}:</span>
        <span className="font-medium">{meta.templateName}</span>
      </div>,
    );
  }

  // User actions
  if (meta.userName || meta.userEmail) {
    rows.push(
      <div key="user" className="flex items-center gap-2">
        <span className="text-muted-foreground">{t("admin.auditLog.details.user")}:</span>
        {meta.userName && <span className="font-medium">{meta.userName}</span>}
        {meta.userEmail && (
          <span className="text-muted-foreground">({meta.userEmail})</span>
        )}
      </div>,
    );
  }

  // Speciality actions
  if (meta.specialityName) {
    rows.push(
      <div key="speciality" className="flex items-center gap-2">
        <span className="text-muted-foreground">{t("admin.auditLog.details.speciality")}:</span>
        <span className="font-medium">{meta.specialityName}</span>
      </div>,
    );
  }

  if (rows.length === 0) return null;

  return <div className="mt-3 pt-3 border-t space-y-1.5 text-sm">{rows}</div>;
}

export default function AuditLog() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  function actionLabel(action: string): string {
    return t(`admin.auditLog.actions.${action}`, { defaultValue: action });
  }

  const { data: response, isLoading } = useAuditLogs({ page });

  const entries = response?.data ?? [];

  const totalSecs = response?.totalTranscriptionSeconds ?? 0;
  const totalMin = Math.floor(totalSecs / 60);
  const totalSecRem = totalSecs % 60;
  const totalLabel =
    totalMin === 0
      ? `${totalSecRem} ${t("admin.auditLog.sec")}`
      : totalSecRem === 0
        ? `${totalMin} ${t("admin.auditLog.min")}`
        : `${totalMin} ${t("admin.auditLog.min")} ${totalSecRem} ${t("admin.auditLog.sec")}`;

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">{t("admin.auditLog.title")}</h1>
        {totalSecs > 0 && (
          <span className="text-sm text-muted-foreground">
            {t("admin.auditLog.totalTranscriptionTime")}:{" "}
            <span className="font-medium text-foreground">{totalLabel}</span>
          </span>
        )}
      </div>

      {isLoading ? (
        <Spinner />
      ) : entries.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {t("common.noResults")}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-2">
            {entries.map((entry) => {
              const isExpanded = expandedId === entry.id;
              const meta = (entry.metadata as AuditMeta | null) ?? null;
              const hasDetails =
                meta?.patientName ||
                meta?.templateName ||
                meta?.userName ||
                meta?.specialityName ||
                (entry.resourceType === "consultation" && entry.resourceId) ||
                (entry.resourceType === "patient" && entry.resourceId);

              return (
                <Card
                  key={entry.id}
                  className={hasDetails ? "cursor-pointer hover:shadow-sm transition-shadow" : ""}
                  onClick={() => {
                    if (hasDetails) {
                      setExpandedId(isExpanded ? null : entry.id);
                    }
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0">
                        {hasDetails ? (
                          isExpanded ? (
                            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                          )
                        ) : (
                          <span className="w-4 shrink-0" />
                        )}
                        <span
                          className={`shrink-0 text-xs px-2 py-1 rounded-full font-medium ${actionColor(entry.action)}`}
                        >
                          {actionLabel(entry.action)}
                        </span>
                        <span className="text-sm text-muted-foreground truncate">
                          {entry.userName ?? entry.userEmail ?? entry.userId ?? "—"}
                        </span>
                        {entry.action === "transcription.audio" && Math.round(Number(meta?.durationSeconds)) > 0 && (
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {(() => {
                              const secs = Math.round(Number(meta?.durationSeconds ?? 0));
                              const m = Math.floor(secs / 60);
                              const s = secs % 60;
                              if (m === 0) return `${s} ${t("admin.auditLog.sec")}`;
                              if (s === 0) return `${m} ${t("admin.auditLog.min")}`;
                              return `${m} ${t("admin.auditLog.min")} ${s} ${t("admin.auditLog.sec")}`;
                            })()}
                          </span>
                        )}
                      </div>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {new Date(entry.createdAt).toLocaleString(undefined, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>

                    {isExpanded && (
                      <ResourceDetails
                        resourceType={entry.resourceType}
                        resourceId={entry.resourceId}
                        metadata={meta}
                        t={t}
                      />
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <Pagination
            page={page}
            totalPages={Math.ceil((response?.total ?? 0) / PAGE_SIZE)}
            totalItems={response?.total ?? 0}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </>
      )}
    </Layout>
  );
}
